import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Briefcase,
  CheckCircle2,
  ClipboardList,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import HorizontalList from "../components/horizontalList";
import { useCfoBundle } from "../context/CfoLanguageContext";
import difHero from "../assets/direccion-innovacion-financiera-hero.jpg";
import { LINKEDIN_JULIAN } from "../data/cfoFraccionalData";
import { DFE_PLANES_PATH, DFE_PDF_PATH, DFE_PRINT_PATH } from "../data/direccionFinancieraPaths";
import {
  alliedTeam,
  OPEN_OPTIONS_CLIENTS_BANNER,
  OPEN_OPTIONS_LOGO,
} from "../data/openOptionsAssets";

const impactIcons = [Wallet, BarChart3, TrendingUp];
const journeyIcons = [ClipboardList, Briefcase, Sparkles];

function CfoFraccional() {
  const { cfo, lang } = useCfoBundle();
  const l = cfo.landing;
  const j = l.journey;
  const a = l.alliance;
  const ts = l.teamSection;
  const pc = l.portfolioComplementario;

  const journeyCtaHref = (phase) => {
    if (phase.ctaType === "link") return phase.ctaHref;
    if (phase.ctaType === "whatsapp-specialized") return cfo.whatsapp.serviciosEspecializados;
    return cfo.whatsapp.diagnostico;
  };

  const teamMembers = alliedTeam
    .filter((member) => !member.allied)
    .map((member) => {
      if (member.id === "julian-herrera") {
        return {
          ...member,
          focus: `${l.bioDegree}. ${member.focus}`,
        };
      }
      const meuTranslated = (l.meuTeam || []).find((item) => item.id === member.id);
      return meuTranslated
        ? {
            ...member,
            name: meuTranslated.name || member.name,
            role: meuTranslated.role || member.role,
            focus: meuTranslated.focus || member.focus,
          }
        : member;
    });

  const journeyCtaExternal = (phase) => phase.ctaType !== "link";

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <section className="relative text-white overflow-hidden min-h-[78vh] lg:min-h-[85vh] flex items-end lg:items-center">
        <img
          src={difHero}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f3f]/88 via-[#0b1f3f]/55 to-[#0b1f3f]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3f]/75 via-transparent to-[#0b1f3f]/25" />
        <div className="relative w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <div className="mb-6">
            <span className="inline-block rounded-full border border-[#9ec5ff]/50 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#9ec5ff] backdrop-blur-sm">
              {l.badge}
            </span>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{l.heroTitle}</h1>
            <p className="mt-5 text-lg sm:text-xl text-[#dbeafe] font-medium leading-relaxed">{l.heroSubtitle}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#ruta"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
              >
                {j.label}
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/empresas/direccion-de-innovacion-financiera/diagnostico"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 px-6 py-3 text-sm font-bold text-white hover:bg-white/10 transition"
              >
                Guía del diagnóstico gratuito
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/empresas/direccion-de-innovacion-financiera/planes"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-6 py-3 text-sm font-bold text-white/90 hover:bg-white/10 transition"
              >
                {l.ctaPlans}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={cfo.whatsapp.cfo}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full border-2 border-white/40 px-6 py-3 text-sm font-semibold text-white/90 hover:bg-white/10 transition"
              >
                {l.ctaInfo}
              </a>
            </div>
            <div className="mt-10 border-l-4 border-[#9ec5ff] pl-5 sm:pl-6 max-w-2xl">
              <p className="text-xl sm:text-2xl text-white font-extrabold leading-snug">{l.heroQuote1}</p>
              <p className="mt-3 text-base sm:text-lg text-white/95 font-medium leading-relaxed">
                {l.heroQuote2}
              </p>
            </div>
          </div>
        </div>
      </section>

      <HorizontalList items={l.nav} even plain />

      <section className="py-16 px-6 sm:px-10 lg:px-16 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] leading-snug max-w-4xl mx-auto">
            {l.whatIsTitle}
          </h2>
          <p className="mt-5 text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed">{l.whatIsBody}</p>
        </div>
      </section>

      <section id="ruta" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-gradient-to-b from-[#e8f2f4] to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{j.label}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] leading-snug">{j.title}</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{j.subtitle}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {j.phases.map((phase, index) => {
              const Icon = journeyIcons[index];
              const isRecommended = phase.recommended;
              const ctaHref = journeyCtaHref(phase);
              const ctaExternal = journeyCtaExternal(phase);
              const CtaTag = ctaExternal ? "a" : Link;
              const ctaProps = ctaExternal
                ? { href: ctaHref, target: "_blank", rel: "noopener noreferrer" }
                : { to: ctaHref };

              return (
                <article
                  key={phase.phase}
                  className={`relative flex flex-col rounded-3xl border bg-white p-6 sm:p-8 shadow-md transition hover:shadow-xl ${
                    isRecommended
                      ? "border-[#1746a2] ring-2 ring-[#1746a2]/20 lg:-translate-y-1"
                      : "border-slate-200"
                  }`}
                >
                  {isRecommended && (
                    <span className="absolute -top-3 left-6 rounded-full bg-[#1746a2] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {j.recommended}
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                        isRecommended ? "bg-[#1746a2] text-white" : "bg-[#1746a2]/10 text-[#1746a2]"
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{phase.phase}</p>
                      <h3 className="text-xl font-extrabold text-[#0b1f3f] leading-tight">{phase.title}</h3>
                    </div>
                  </div>

                  {phase.summary && (
                    <p className="mb-5 text-sm text-slate-600 leading-relaxed">{phase.summary}</p>
                  )}

                  {phase.deliverables?.length > 0 && (
                    <div className="mb-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                        {j.deliverablesLabel}
                      </p>
                      <ul className="space-y-2">
                        {phase.deliverables.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="h-4 w-4 text-[#1746a2] shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {phase.description && (
                    <p className="mb-5 text-sm text-slate-600 leading-relaxed flex-1">{phase.description}</p>
                  )}

                  {phase.bridge && (
                    <p className="mb-5 rounded-2xl bg-[#1746a2]/5 border border-[#1746a2]/15 px-4 py-3 text-sm text-slate-700 leading-relaxed">
                      {phase.bridge}
                    </p>
                  )}

                  <div className="mt-auto pt-2 space-y-2">
                    <CtaTag
                      {...ctaProps}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition ${
                        isRecommended
                          ? "bg-[#1746a2] text-white hover:bg-[#123a72]"
                          : "border-2 border-[#1746a2] text-[#1746a2] hover:bg-[#1746a2]/5"
                      }`}
                    >
                      {phase.cta}
                      <ArrowRight className="h-4 w-4" />
                    </CtaTag>
                    {phase.secondaryCta && phase.secondaryCtaHref && (
                      <Link
                        to={phase.secondaryCtaHref}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#1746a2] hover:bg-slate-50 transition"
                      >
                        {phase.secondaryCta}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="reto" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-amber-700">{l.challengeLabel}</p>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] leading-snug">{l.challengeTitle}</h2>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed">{l.challengeBody}</p>
            </div>
            <ul className="space-y-4">
              {l.challenges.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow-sm"
                >
                  <span className="text-amber-500 font-bold text-lg leading-none mt-0.5">!</span>
                  <p className="text-slate-700 font-medium">{item}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12 rounded-2xl bg-[#1746a2] overflow-hidden shadow-xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/25">
              {l.metrics.map((metrica) => (
                <div key={metrica.etiqueta} className="px-6 py-10 sm:py-12 text-center text-white">
                  <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">{metrica.valor}</p>
                  <p className="mt-4 text-xs sm:text-sm font-semibold uppercase tracking-wide text-white/90 leading-snug max-w-xs mx-auto">
                    {metrica.etiqueta}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[10px] sm:text-xs text-white/55 text-center px-4 py-3 border-t border-white/15 leading-relaxed">
              {l.sourcesLabel} {l.metrics.map((m) => m.fuente).join(" · ")}
            </p>
          </div>
        </div>
      </section>

      <section id="solucion" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#1746a2] text-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#9ec5ff]" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#9ec5ff]">{l.solutionLabel}</p>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold max-w-2xl">{l.solutionTitle}</h2>
          <p className="mt-5 text-[#dbeafe] text-lg max-w-3xl leading-relaxed">{l.solutionBody}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {l.solutionItems.map((item) => (
              <div key={item} className="flex items-start gap-2 rounded-2xl bg-white/10 border border-white/20 px-5 py-4">
                <CheckCircle2 className="w-5 h-5 text-[#9ec5ff] shrink-0 mt-0.5" />
                <p className="font-medium text-sm">{item}</p>
              </div>
            ))}
          </div>
          <Link
            to="/empresas/direccion-de-innovacion-financiera/planes"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-[#123a72] font-bold hover:bg-[#e8f2ff] transition"
          >
            {l.ctaPlansFull}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section id="impacto" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#e8f2f4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{l.impactLabel}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{l.impactTitle}</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{l.impactSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {l.impactCards.map((card, index) => {
              const Icon = impactIcons[index];
              return (
                <article
                  key={card.titulo}
                  className="rounded-3xl bg-white p-8 shadow-md border border-white hover:shadow-lg transition"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#1746a2]/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#1746a2]" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{card.metrica}</p>
                  <h3 className="mt-2 text-xl font-extrabold text-[#0b1f3f]">{card.titulo}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{card.descripcion}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="alianza" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{a.label}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{a.title}</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{a.body}</p>
            <a
              href={a.openOptionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#1746a2] hover:text-[#123a72] hover:underline underline-offset-2"
            >
              {a.openOptionsCta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <article className="rounded-3xl border border-[#1746a2]/20 bg-[#1746a2]/5 p-8">
              <div className="flex flex-wrap items-center gap-4 mb-5">
                <img src={OPEN_OPTIONS_LOGO} alt="Open Options" className="h-10 w-auto object-contain" />
                <span className="inline-flex items-center rounded-full bg-[#1746a2] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                  {a.alliedBadge}
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-4">{a.openOptionsTagline}</p>
              <h3 className="text-xl font-extrabold text-[#0b1f3f] mb-3">{a.taskForceTitle}</h3>
              <p className="text-slate-700 leading-relaxed">{a.taskForceBody}</p>
            </article>

            <article className="rounded-3xl border border-slate-200 bg-slate-50 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1746a2]/10 text-[#1746a2]">
                  <Users className="h-6 w-6" />
                </div>
                <p className="text-xl font-extrabold text-[#0b1f3f]">{a.clientsLabel}</p>
              </div>
              {a.clientsIntro && <p className="text-sm text-slate-600 mb-5 leading-relaxed">{a.clientsIntro}</p>}
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <img
                  src={OPEN_OPTIONS_CLIENTS_BANNER}
                  alt={a.clientsLabel}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="equipo" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{ts.label}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{ts.title}</h2>
            <p className="mt-4 text-slate-600 max-w-3xl mx-auto leading-relaxed">{ts.subtitle}</p>
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2] mb-6 text-center">
            {ts.unifiedLabel}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {teamMembers.map((member) => (
              <article
                key={member.id}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-[#1746a2]/30 hover:shadow-md transition flex flex-col items-center text-center"
              >
                <img
                  src={member.photo}
                  alt={member.name}
                  className="w-28 h-28 rounded-full object-cover border-4 border-[#1746a2]/10 shadow-md mb-4"
                  style={{ objectPosition: member.photoPosition || "50% top" }}
                  loading="lazy"
                />
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-2 ${
                    member.allied
                      ? "border border-[#1746a2]/30 bg-[#1746a2]/5 text-[#1746a2]"
                      : "border border-[#0b1f3f]/20 bg-[#0b1f3f]/5 text-[#0b1f3f]"
                  }`}
                >
                  {member.allied ? `${member.org} · ${a.alliedBadge}` : member.org}
                </span>
                <p className="text-lg font-extrabold text-[#0b1f3f] leading-snug">{member.name}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-wide text-[#1746a2]">{member.role}</p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{member.focus}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 rounded-3xl border border-[#1746a2]/20 bg-gradient-to-br from-[#f4f7ff] to-white p-7 sm:p-9">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-7">
              <img src={OPEN_OPTIONS_LOGO} alt="Open Options" className="h-12 w-auto object-contain self-start" />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">
                  {ts.openOptionsExperienceLabel}
                </p>
                <h3 className="mt-1 text-2xl font-extrabold text-[#0b1f3f]">
                  {ts.openOptionsExperienceTitle}
                </h3>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6">{ts.openOptionsExperienceIntro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {a.clients.map((experience) => (
                <div
                  key={experience}
                  className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#1746a2]" />
                  <p className="text-sm leading-relaxed text-slate-700">{experience}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            id="contacto"
            className="scroll-mt-24 mt-12 rounded-3xl border border-slate-200 bg-gradient-to-br from-[#0b1f3f] to-[#123a72] p-8 sm:p-10 text-white"
          >
            <p className="text-sm font-bold uppercase tracking-widest text-[#9ec5ff] mb-6 text-center">
              {l.contactTitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-white/70 mb-1.5">{l.emailLabel}</p>
                <a href="mailto:colaboradores@meu-aliado.com" className="block text-[#dbeafe] hover:text-white hover:underline break-all">
                  colaboradores@meu-aliado.com
                </a>
                <a href="mailto:julianherrx@hotmail.com" className="block text-[#dbeafe] hover:text-white hover:underline break-all mt-1">
                  julianherrx@hotmail.com
                </a>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-white/70 mb-1.5">{l.phoneLabel}</p>
                <a href="tel:+573213213635" className="block text-[#dbeafe] hover:text-white hover:underline">
                  {lang === "es" ? "Cel:" : "Mobile:"} +57 321 321 3635
                </a>
                <a href="https://wa.me/573213213635" target="_blank" rel="noopener noreferrer" className="block text-[#dbeafe] hover:text-white hover:underline mt-1">
                  WhatsApp: +57 321 321 3635
                </a>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-white/70 mb-1.5">{l.linkedinLabel}</p>
                <a href={LINKEDIN_JULIAN} target="_blank" rel="noopener noreferrer" className="text-[#dbeafe] hover:text-white hover:underline break-all">
                  linkedin.com/in/julian-herrera-09b361231
                </a>
              </div>
              <div className="flex flex-col justify-center">
                <a
                  href={cfo.whatsapp.cfo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white hover:bg-[#1da851] transition"
                >
                  <FaWhatsapp className="h-5 w-5 shrink-0" aria-hidden />
                  {l.ctaAdvisory}
                </a>
              </div>
            </div>
            <p className="text-xs text-[#dbeafe]/80 text-center leading-relaxed mt-6">{l.contactNote}</p>
          </div>
        </div>
      </section>

      <section id="portafolio" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#e8f2f4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{pc.label}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{pc.title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{pc.subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href={DFE_PDF_PATH}
                download
                className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-8 py-3 text-sm font-bold text-white hover:bg-[#123a72] transition shadow-md"
              >
                {pc.downloadPdf}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={DFE_PRINT_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[#1746a2] bg-white px-6 py-3 text-sm font-bold text-[#1746a2] hover:bg-[#1746a2]/5 transition"
              >
                {lang === "en" ? "View printable version" : "Ver versión imprimible"}
              </a>
            </div>
            {pc.downloadPdfHint && (
              <p className="mt-3 text-xs text-slate-500">{pc.downloadPdfHint}</p>
            )}
          </div>

          <article className="rounded-3xl border border-[#1746a2]/25 bg-white p-8 shadow-sm mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1746a2] text-white">
                <Wallet className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-extrabold text-[#0b1f3f]">{pc.meuTitle}</h3>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {pc.meuItems.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700 rounded-xl bg-[#1746a2]/5 px-4 py-3">
                  <CheckCircle2 className="w-4 h-4 text-[#1746a2] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to={DFE_PLANES_PATH}
              className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#123a72] transition"
            >
              {pc.meuCta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </article>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <img src={OPEN_OPTIONS_LOGO} alt="Open Options" className="h-9 w-auto object-contain" />
            <span className="inline-flex items-center rounded-full border border-[#1746a2]/30 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#1746a2]">
              {a.alliedBadge}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {pc.pillars.map((pillar) => (
              <article key={pillar.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-extrabold text-[#0b1f3f] mb-4">{pillar.title}</h3>
                <ul className="space-y-2">
                  {pillar.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[#1746a2] shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={a.openOptionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#1746a2] bg-white px-8 py-3 text-sm font-bold text-[#1746a2] hover:bg-[#1746a2]/5 transition"
            >
              {pc.openOptionsCta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CfoFraccional;
